import React, { useMemo } from "react"

import { Input } from "@coval/ui/input"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import {
  JSON_TEMPLATE_FIELDS,
  validateJsonTemplate,
} from "@/utils/validation/jsonTemplateValidation"

import { CHAT_WEBSOCKET_TOOLTIPS } from "./tooltips"
import type { ChatWebSocketFieldProps } from "./types"

export const TextMessageFormatFields: React.FC<ChatWebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  const textMessageTypeValues = Array.isArray(metadata.text_message_type_values)
    ? metadata.text_message_type_values
    : typeof metadata.text_message_type_values === "string"
      ? metadata.text_message_type_values
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : []

  const sendMessageTemplateError = useMemo(
    () =>
      validateJsonTemplate(
        metadata.send_message_template,
        JSON_TEMPLATE_FIELDS.message,
      ),
    [metadata.send_message_template],
  )

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">
        Text Message Format
      </h4>

      {/* Send Message Template */}
      <div className="space-y-2">
        <FieldLabel tooltip={CHAT_WEBSOCKET_TOOLTIPS.sendMessageTemplate}>
          Send Message Template
        </FieldLabel>
        <JsonCodeEditor
          hideGutters
          copyable
          ariaLabel="Send Message Template"
          placeholder={'{\n  "type": "message",\n  "text": "{{message}}"\n}'}
          value={metadata.send_message_template || ""}
          onChange={(value) =>
            onMetadataUpdate({ send_message_template: value })
          }
          rows={4}
          disabled={disabled}
          error={sendMessageTemplateError}
          // The inline error covers parse failures; editor lint would repeat it.
          lint={false}
        />
      </div>

      {/* Receive Message Text Path */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="receive-text-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.receiveMessageTextPath}
        >
          Receive Message Text Path
        </FieldLabel>
        <Input
          id="receive-text-path"
          aria-label="Receive Message Text Path"
          value={metadata.receive_message_text_path || ""}
          placeholder="Enter the received text path"
          onChange={(e) =>
            onMetadataUpdate({ receive_message_text_path: e.target.value })
          }
          disabled={disabled}
        />
      </div>

      {/* Receive Message Type Path */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="receive-type-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.receiveMessageTypePath}
        >
          Receive Message Type Path
        </FieldLabel>
        <Input
          id="receive-type-path"
          aria-label="Receive Message Type Path"
          value={metadata.receive_message_type_path || ""}
          placeholder="Enter the received message type path"
          onChange={(e) =>
            onMetadataUpdate({ receive_message_type_path: e.target.value })
          }
          disabled={disabled}
        />
      </div>

      {/* Text Message Type Values */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="text-message-types"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.textMessageTypeValues}
        >
          Text Message Type Values
        </FieldLabel>
        <Input
          id="text-message-types"
          aria-label="Text Message Type Values"
          value={textMessageTypeValues.join(", ")}
          placeholder="Enter text message types, separated by commas"
          onChange={(e) =>
            onMetadataUpdate({
              text_message_type_values: e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
            })
          }
          disabled={disabled}
        />
      </div>

      {/* Agent Disconnect Message Type */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="disconnect-type"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.agentDisconnectMessageType}
        >
          Agent Disconnect Message Type
        </FieldLabel>
        <Input
          id="disconnect-type"
          aria-label="Agent Disconnect Message Type"
          value={metadata.agent_disconnect_message_type || ""}
          placeholder="Enter the disconnect message type"
          onChange={(e) =>
            onMetadataUpdate({ agent_disconnect_message_type: e.target.value })
          }
          disabled={disabled}
        />
      </div>

      {/* Direction Filter Fields */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="direction-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.receiveMessageDirectionPath}
        >
          Receive Message Direction Path
        </FieldLabel>
        <Input
          id="direction-path"
          aria-label="Receive Message Direction Path"
          value={metadata.receive_message_direction_path || ""}
          placeholder="Enter the received direction path"
          onChange={(e) =>
            onMetadataUpdate({
              receive_message_direction_path: e.target.value,
            })
          }
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <FieldLabel
          htmlFor="outbound-direction"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.outboundDirectionValue}
        >
          Outbound Direction Value
        </FieldLabel>
        <Input
          id="outbound-direction"
          aria-label="Outbound Direction Value"
          value={metadata.outbound_direction_value || ""}
          placeholder="Enter the outbound direction value"
          onChange={(e) =>
            onMetadataUpdate({ outbound_direction_value: e.target.value })
          }
          disabled={disabled}
        />
      </div>
    </div>
  )
}
